import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import api from '@/api/client';
import { cn } from '@/lib/cn';
import { isInterviewer } from '@/lib/roleRouting';
import { capsForRole } from '@/lib/permissions';
import { offerEditorHref } from '@/components/offers/panel/useStartOffer';
import { useAuthStore } from '@/store/authStore';

interface Notif { id: string; type: string; title: string; body: string; read: boolean; metadata: any; createdAt: string; }

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications')).data.data as { notifications: Notif[]; unread: number },
    refetchInterval: 30000,
  });
  const unread = data?.unread ?? 0;
  const notifs = data?.notifications ?? [];

  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const openNotif = (n: Notif) => {
    if (!n.read) markRead.mutate(n.id);
    const attemptId = n.metadata?.attemptId;
    setOpen(false);
    // Interview notices (panel added, moved, cancelled, scorecard due, all cards in, needs
    // outcome) open the round: its panelists get the interview room, and staff who are not on
    // its panel are taken on to the board with that application open (InterviewRoomPage).
    // They exist only with Interviews on, so nothing older changes.
    if (n.metadata?.kind === 'interview' && typeof n.metadata.roundId === 'string' && n.metadata.roundId) {
      navigate(`/admin/interviews/rounds/${encodeURIComponent(n.metadata.roundId)}`);
      return;
    }
    // An Interviewer's bell holds interview notices only (the server's fence); nothing in it
    // ever leads to a report or a staff page.
    if (isInterviewer(role)) return;
    if (attemptId) navigate(`/admin/reports/${attemptId}`);
    // An accepted offer's follow-up that failed ("Stage rules": "… Open the offer") opens the
    // offer form, for those who can open it (its route needs manageAssessments).
    // Only that notice: the other offer notices predate this and stay where they were.
    else if (n.metadata?.kind === 'offer' && n.metadata.event === 'follow-up-failed' && typeof n.metadata.offerId === 'string' && n.metadata.offerId) {
      if (capsForRole(role).includes('manageAssessments')) navigate(offerEditorHref(n.metadata.offerId, location));
    }
    // Account notices ("Team rules": who added, changed or deactivated whom) go to the
    // Team page. They exist only with that switch on, so nothing older changes.
    else if (n.metadata?.kind === 'account') navigate('/admin/team');
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="relative p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-50 transition-colors" aria-label="Notifications">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-[#ECE7E3] shadow-elevated z-40 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#F1ECE8]">
                <h3 className="font-display font-bold text-ink-900 text-sm">Notifications</h3>
                {unread > 0 && (
                  <button onClick={() => readAll.mutate()} className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1">
                    <CheckCheck className="w-3.5 h-3.5" />Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifs.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-ink-400">You're all caught up 🎉</div>
                ) : notifs.map((n) => (
                  <button key={n.id} onClick={() => openNotif(n)} className={cn('w-full text-left px-4 py-3 border-b border-[#F1ECE8] hover:bg-surface-50 transition-colors', !n.read && 'bg-primary-50/40')}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />}
                      <div className={cn('min-w-0 flex-1', n.read && 'pl-4')}>
                        <p className="text-sm font-semibold text-ink-800 truncate">{n.title}</p>
                        <p className="text-xs text-ink-500 truncate">{n.body}</p>
                        <p className="text-[11px] text-ink-300 mt-0.5">{formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
